import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import NotesIcon from '@mui/icons-material/Notes';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  maxWidth: '800px',
  margin: '0 auto',
  backgroundColor: '#fff',
}));

const EditMeeting = () => {
  const navigate = useNavigate();
  const { meetingId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [meeting, setMeeting] = useState(null);

  // Telly employees
  const [tellyList, setTellyList] = useState([]);
  const [tellyLoading, setTellyLoading] = useState(false);

  const { isTelly, employeeData } = useAdminRole();
  const selfTellyLabel = employeeData?.name ? `Self (You – ${employeeData.name})` : 'Self (You)';
  const showSelfTellyOption = isTelly && employeeData?.id;

  const [formState, setFormState] = useState({
    title: '',
    meeting_date: '',
    meeting_time: '',
    type: '',
    status: '',
    telly_employee_id: null,
    comment: '',
    presentation_show: false,
  });

  // Fetch meeting on mount
  useEffect(() => {
    const fetchMeeting = async () => {
      setLoading(true);
      try {
        const response = await ApiService.get(`/admin/meeting/get-meeting/${meetingId}`);
        const data = response?.data?.meeting || response?.meeting || response?.data || response;

        if (!data || !data.id) {
          throw new Error('Meeting not found');
        }

        setMeeting(data);

        // Parse date and time from meeting data
        let meetingDate = '';
        let meetingTime = '';

        if (data.meeting_date) {
          meetingDate = new Date(data.meeting_date).toISOString().split('T')[0];
        } else if (data.date_time) {
          const dt = new Date(data.date_time);
          meetingDate = dt.toISOString().split('T')[0];
          meetingTime = dt.toTimeString().slice(0, 5);
        }

        setFormState({
          title: data.title || '',
          meeting_date: meetingDate,
          meeting_time: meetingTime,
          type: data.type || '',
          status: data.status || 'scheduled',
          telly_employee_id: data.telly_employee_id || null,
          comment: data.comment || '',
          presentation_show: data.presentation_show || false,
        });

      } catch (err) {
        logger.error('Error fetching meeting:', err);
        setError('Failed to load meeting details');
      } finally {
        setLoading(false);
      }
    };

    if (meetingId) {
      fetchMeeting();
    }
  }, [meetingId]);

  // Fetch Telly employees on mount
  useEffect(() => {
    const fetchTellyList = async () => {
      setTellyLoading(true);
      try {
        const response = await ApiService.get('/admin/employee/get-telly-employees');
        const data = response?.data?.data || response?.data || response || [];
        const list = Array.isArray(data) ? data : [];
        setTellyList(list);
      } catch (err) {
        logger.error('Error fetching Telly list:', err);
        setTellyList([]);
      } finally {
        setTellyLoading(false);
      }
    };
    fetchTellyList();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!formState.title || !formState.meeting_date) {
        throw new Error('Title and meeting date are required');
      }

      const dateTime = formState.meeting_time
        ? `${formState.meeting_date} ${formState.meeting_time}`
        : `${formState.meeting_date} 10:00`;

      const payload = {
        title: formState.title,
        meeting_date: formState.meeting_date,
        date_time: dateTime,
        type: formState.type || 'General',
        status: formState.status || 'scheduled',
        telly_employee_id: formState.telly_employee_id || null,
        comment: formState.comment || null,
        presentation_show: formState.presentation_show,
      };

      await ApiService.put(`/admin/meeting/update-meeting/${meetingId}`, payload);

      setSuccess('Meeting updated successfully!');
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate(`/admin-dashboard/meeting/${meetingId}`);
      }, 1500);

    } catch (err) {
      logger.error('Failed to update meeting:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to update meeting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !meeting) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)',
            }}
          >
            <EditIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Edit Meeting
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update meeting details and reschedule
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Box>

      <FormContainer>
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Lead Info (Read Only) */}
          {meeting?.leadDetails && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f0fdf4', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="success.dark" fontWeight={600} sx={{ mb: 1 }}>
                Lead Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Contact:</strong> {meeting.leadDetails.contact_person || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Business:</strong> {meeting.leadDetails.business_name || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {meeting.leadDetails.phone_number || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {meeting.leadDetails.email || 'N/A'}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Meeting Details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <EventIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Meeting Details</Typography>
          </Box>

          <TextField
            fullWidth
            label="Meeting Title"
            name="title"
            value={formState.title}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
            placeholder="e.g., Follow-up Discussion, Demo Presentation"
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              label="Meeting Date"
              name="meeting_date"
              type="date"
              value={formState.meeting_date}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Meeting Time"
              name="meeting_time"
              type="time"
              value={formState.meeting_time}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <AccessTimeIcon sx={{ color: '#9ca3af', mr: 1 }} />,
              }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Meeting Type</InputLabel>
              <Select
                name="type"
                value={formState.type}
                label="Meeting Type"
                onChange={handleChange}
              >
                <MenuItem value="General">General</MenuItem>
                <MenuItem value="Sales">Sales</MenuItem>
                <MenuItem value="Demo">Demo</MenuItem>
                <MenuItem value="Follow-up">Follow-up</MenuItem>
                <MenuItem value="Onboarding">Onboarding</MenuItem>
                <MenuItem value="Support">Support</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formState.status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="RESCHEDULE">RESCHEDULE</MenuItem>
                <MenuItem value="NOT AVAILABLE">NOT AVAILABLE</MenuItem>
                <MenuItem value="NOT INTERESTED">NOT INTERESTED</MenuItem>
                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Telly Assignment */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
            <PhoneIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Assign Telly Employee</Typography>
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Assign Telly</InputLabel>
            <Select
              name="telly_employee_id"
              value={formState.telly_employee_id || ''}
              label="Assign Telly"
              onChange={handleChange}
              disabled={tellyLoading}
              startAdornment={<PersonIcon sx={{ color: '#9ca3af', mr: 1 }} />}
            >
              <MenuItem value="">
                <em>{tellyLoading ? 'Loading...' : (tellyList.length === 0 ? 'No Telly available' : 'Select Telly Employee')}</em>
              </MenuItem>
              {showSelfTellyOption && (
                <MenuItem key="telly-self" value={employeeData.id}>
                  {selfTellyLabel}
                </MenuItem>
              )}
              {tellyList.map((telly) => (
                <MenuItem key={telly.id} value={telly.id}>
                  {[telly.first_name, telly.last_name].filter(Boolean).join(' ') || telly.email || `Employee #${telly.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Notes */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 3 }}>
            <NotesIcon sx={{ color: '#6366f1' }} />
            <Typography variant="subtitle1" fontWeight={600}>Notes</Typography>
          </Box>

          <TextField
            fullWidth
            label="Meeting Notes/Comments"
            name="comment"
            value={formState.comment}
            onChange={handleChange}
            multiline
            rows={3}
            sx={{ mb: 3 }}
            placeholder="Add any relevant notes about this meeting..."
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                },
              }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </FormContainer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditMeeting;
